/**
 * Keto domain rules — web mirror of utils/food-utils.ts +
 * constants/keto-targets.ts. The SERVER recomputes all math for AI scans
 * (functions/src/utils/sanitizeAnalysis.ts); these mirrors cover local edits,
 * manual entries, and display.
 */
import type {
  DailyKetoTargets,
  KetoStrictness,
  KetoVerdictLevel,
  UserProfile,
} from '@/types'

/** Daily net-carb budget (g) per strictness level. */
export const NET_CARBS_BY_STRICTNESS: Record<KetoStrictness, number> = {
  strict: 20,
  moderate: 25,
  dirty: 50,
}

/** Fallback when no strictness is set anywhere. */
export const DEFAULT_NET_CARBS_G = 25

/**
 * Net carbs for one food: max(0, carbs − fiber − sugarAlcohols × factor).
 * Erythritol and allulose subtract fully; other sugar alcohols at half weight.
 */
export function computeNetCarbsG(
  carbsG: number,
  fiberG: number,
  sugarAlcoholsG = 0,
  sugarAlcoholType?: string,
): number {
  const fullSubtract = sugarAlcoholType === 'erythritol' || sugarAlcoholType === 'allulose'
  const factor = fullSubtract ? 1 : 0.5
  return Math.max(0, carbsG - fiberG - sugarAlcoholsG * factor)
}

/**
 * Per-food keto rating from net carbs (per serving as eaten).
 * Server thresholds: ≤3g keto, ≤7g borderline, >7g not_keto.
 */
export function deriveKetoRating(netCarbsG: number): KetoVerdictLevel {
  if (netCarbsG <= 3) return 'keto'
  if (netCarbsG <= 7) return 'borderline'
  return 'not_keto'
}

/**
 * Meal-level verdict vs the strictness budget (server thresholds):
 * strict >6g borderline / >10g not_keto; moderate >10/>15; dirty >15/>20.
 */
export function deriveMealVerdict(
  totalNetCarbsG: number,
  strictness: KetoStrictness = 'moderate',
): KetoVerdictLevel {
  const [borderlineAt, notKetoAt] =
    strictness === 'strict' ? [6, 10] : strictness === 'dirty' ? [15, 20] : [10, 15]
  if (totalNetCarbsG > notKetoAt) return 'not_keto'
  if (totalNetCarbsG > borderlineAt) return 'borderline'
  return 'keto'
}

/**
 * Effective daily targets — mirror of constants/keto-targets.ts
 * getKetoDailyTargets(). Explicit macroTargets win, then strictness budget,
 * then the 25g default; user-set optional limits override.
 */
export function getKetoDailyTargets(
  profile?: Pick<
    UserProfile,
    'macroTargets' | 'ketoStrictness' | 'glycemicTargets' | 'optionalNutrients'
  > | null,
): DailyKetoTargets {
  const macro = profile?.macroTargets
  const strictness = profile?.ketoStrictness
  const netCarbsG =
    macro?.netCarbsG ??
    (strictness ? NET_CARBS_BY_STRICTNESS[strictness] : undefined) ??
    DEFAULT_NET_CARBS_G

  const targets: DailyKetoTargets = {
    netCarbsG,
    fiberG: profile?.glycemicTargets?.fiberG ?? 25,
    sugarG: profile?.glycemicTargets?.sugarG ?? Math.min(25, netCarbsG),
  }

  if (macro?.calories) targets.calories = macro.calories
  if (macro?.proteinG) targets.proteinG = macro.proteinG
  if (macro?.fatG) targets.fatG = macro.fatG

  // User-set optional nutrient limits still win (premium feature)
  const opt = profile?.optionalNutrients
  if (opt?.dailyCaloriesLimit) targets.calories = opt.dailyCaloriesLimit
  if (opt?.dailyProteinLimit) targets.proteinG = opt.dailyProteinLimit
  if (opt?.dailyFatLimit) targets.fatG = opt.dailyFatLimit

  return targets
}

// Conversion helpers (mirror types/weight.ts + types/water.ts)

export function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462 * 10) / 10
}

// 0.001 kg so a weight typed in lbs reads back exactly at 0.1 lb precision
export function lbsToKg(lbs: number): number {
  return Math.round((lbs / 2.20462) * 1000) / 1000
}

const OZ_PER_ML = 0.033814

export function mlToOz(ml: number): number {
  return Math.round(ml * OZ_PER_ML * 10) / 10
}

export function ozToMl(oz: number): number {
  return Math.round(oz / OZ_PER_ML)
}

export const DEFAULT_WATER_GOAL_ML = 2500

/** Quick-add row on the water tracker card (330ml / 12oz ≈ 1 can). */
export const QUICK_ADD_AMOUNTS_ML = [100, 250, 330, 500] as const
export const QUICK_ADD_AMOUNTS_OZ = [4, 8, 12, 16] as const

/** Sanity cap for a single exercise entry. */
export const MAX_EXERCISE_CALORIES = 5000

/** Blood-ketone zones (mmol/L) for display coloring. */
export function ketoneZone(valueMmolL: number): 'none' | 'trace' | 'optimal' | 'high' {
  if (valueMmolL < 0.5) return 'none'
  if (valueMmolL < 1.0) return 'trace'
  if (valueMmolL <= 3.0) return 'optimal'
  return 'high'
}
